import type { IconKind } from '../components/Icon/Icon';
import type { LiveCallDirection, LiveCallSentiment } from './types';

export const LM_PHASES = [
  'greet',
  'identify',
  'intent',
  'verify',
  'resolve',
  'confirm',
] as const;

export const LM_TOKENS = {
  green: '#0F5C45',
  greenDeep: '#0A4530',
  greenSoft: 'rgba(15, 92, 69, 0.10)',
  greenWash: 'rgba(15, 92, 69, 0.05)',
  brass: '#A8845C',
  brassDeep: '#8B6B47',
  brassTint: '#F0E9DC',
  negative: '#A8442E',
  negativeSoft: 'rgba(168, 68, 46, 0.10)',
  warning: '#B5803D',
  warningSoft: 'rgba(181, 128, 61, 0.12)',
  ink3: '#75706A',
  ink4: '#A8A39B',
  ink5: '#D9D5CC',
  iriLinear:
    'linear-gradient(120deg, #4D2855 0%, #8E3567 28%, #D8625F 55%, #D49545 80%, #0F5C45 100%)',
  iriDot: 'linear-gradient(135deg, #4D2855 0%, #D8625F 55%, #D49545 100%)',
  shadowXs: '0 1px 1px rgba(28,24,18,0.04)',
  shadowMd: '0 1px 3px rgba(28,24,18,0.05), 0 4px 12px rgba(28,24,18,0.06), 0 8px 24px rgba(28,24,18,0.05)',
  shadowDrawer: '-24px 0 56px rgba(28,24,18,0.14), -4px 0 14px rgba(28,24,18,0.07)',
} as const;

export const DIRECTION_CONFIG: Record<
  LiveCallDirection,
  { label: string; short: string; color: string }
> = {
  inbound: { label: 'Inbound', short: 'Inbound', color: '#0F5C45' },
  'outbound-patient': { label: 'Out · Patient', short: 'Out · Pt', color: '#8E3567' },
  'outbound-insurance': { label: 'Out · Insurance', short: 'Out · Ins.', color: '#B5803D' },
};

export const SENTIMENT_CONFIG: Record<LiveCallSentiment, { label: string; color: string }> = {
  positive: { label: 'Positive', color: '#0F5C45' },
  neutral: { label: 'Neutral', color: '#A8A39B' },
  frustrated: { label: 'Frustrated', color: '#A8442E' },
  anxious: { label: 'Anxious', color: '#B5803D' },
};

export interface LiveMonitorKpiItem {
  key: string;
  label: string;
  value: number | string;
  delta: string;
  deltaColor: 'positive' | 'warning' | 'neutral';
  icon: IconKind;
}

export const KPI_DATA: LiveMonitorKpiItem[] = [
  {
    key: 'handled',
    label: 'Handled today',
    value: 24,
    delta: '+4 vs yesterday',
    deltaColor: 'positive',
    icon: 'phone-in',
  },
  {
    key: 'booked',
    label: 'Booked',
    value: 21,
    delta: '+3 vs yesterday',
    deltaColor: 'positive',
    icon: 'calendar',
  },
  {
    key: 'escalated',
    label: 'Escalated',
    value: 3,
    // Down is good here (fix-list #4): one fewer escalation is money-green,
    // not amber. Polarity is declared by the metric, never inferred from sign.
    delta: '−1 vs yesterday',
    deltaColor: 'positive',
    icon: 'alert',
  },
  {
    key: 'avg',
    label: 'Avg handle',
    value: '2:47',
    delta: '12s faster',
    deltaColor: 'positive',
    icon: 'clock',
  },
];

export const HOUR_VOLUME = [2, 3, 2, 4, 3, 5, 4, 6, 5, 7, 6, 7];
